import React, { useState, useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Plane, MapPin, X, ChevronLeft, ChevronRight, Loader2, Sparkles } from 'lucide-react';
import { TiltCard } from '../components/home/TiltCard';
import { ScrollDepthReveal } from '../components/home/ScrollDepthReveal';

const TravelPage = () => {
  const [travelItems, setTravelItems] = useState([]);
  const [loadingTravel, setLoadingTravel] = useState(true);
  const [lightbox, setLightbox] = useState(null); // { items, index }

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchTravel();
  }, []);

  const fetchTravel = async () => {
    setLoadingTravel(true);
    try {
      const r = await fetch(`${API_URL}/api/gallery/travel`);
      if (r.ok) setTravelItems(await r.json());
    } catch (e) {
      console.error('Failed to fetch travel gallery', e);
    } finally {
      setLoadingTravel(false);
    }
  };

  const openLightbox = (items, index) => setLightbox({ items, index });
  const closeLightbox = () => setLightbox(null);
  const nextImage = () => setLightbox((l) => ({ ...l, index: (l.index + 1) % l.items.length }));
  const prevImage = () => setLightbox((l) => ({ ...l, index: (l.index - 1 + l.items.length) % l.items.length }));

  const renderGrid = (items) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {items.map((item, index) => (
        <ScrollDepthReveal key={item.gallery_id} delay={Math.min(index * 0.05, 0.3)}>
          <TiltCard maxTilt={12} hoverScale={1.04} testId={`travel-tilt-${index}`}>
            <div
              onClick={() => openLightbox(items, index)}
              className="group relative cursor-pointer overflow-hidden rounded-2xl aspect-[4/3] bg-neutral-900"
              data-testid={`travel-item-${index}`}
            >
              <img
                src={item.image_url}
                alt={item.title || item.description}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent opacity-70 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="absolute inset-0 flex flex-col justify-end p-5 translate-y-0 transition-all duration-500">
                {item.title && (
                  <h3 className="text-lg font-bold text-white mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {item.title}
                  </h3>
                )}
                {item.location && (
                  <p className="text-xs text-neutral-300 flex items-center gap-1 mb-1.5">
                    <MapPin size={12} />
                    {item.location}
                  </p>
                )}
                <p className="text-sm text-neutral-300 line-clamp-3">{item.description}</p>
              </div>
            </div>
          </TiltCard>
        </ScrollDepthReveal>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0c]">
      <Header />
      <main className="pt-20">
        {/* Hero */}
        <section className="relative py-20 lg:py-28 overflow-hidden">
          <div className="absolute top-20 left-10 w-96 h-96 bg-white/[0.05] rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-white/[0.05] rounded-full blur-3xl" />
          <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <span className="inline-flex items-center px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full text-neutral-300 text-sm font-medium mb-6">
              <Sparkles size={14} className="mr-2" />Postcards from the road
            </span>
            <h1 className="text-5xl lg:text-7xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              <span className="bg-gradient-to-r from-white via-neutral-300 to-neutral-500 bg-clip-text text-transparent">Travel</span>
            </h1>
            <p className="text-lg text-neutral-400 max-w-3xl mx-auto leading-relaxed">
              The places I've wandered — stitched together from flights, trains, and long walks.
            </p>
          </div>
        </section>

        {/* Travel Section */}
        <section className="py-16 lg:py-20" data-testid="travel-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-8">
              <div className="p-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/10">
                <Plane className="text-white" size={24} />
              </div>
              <div>
                <h2 className="text-3xl lg:text-4xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Travel
                </h2>
                <p className="text-sm text-neutral-500 mt-0.5">Moments from the journey.</p>
              </div>
            </div>

            {loadingTravel ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-neutral-400" />
              </div>
            ) : travelItems.length === 0 ? (
              <div className="text-center py-16 bg-white/5 rounded-2xl border border-white/10">
                <Plane size={48} className="mx-auto text-neutral-500 mb-4" />
                <p className="text-neutral-400">No travel photos yet. Check back soon!</p>
              </div>
            ) : (
              renderGrid(travelItems)
            )}
          </div>
        </section>
      </main>

      {/* Lightbox */}
      {lightbox && lightbox.items[lightbox.index] && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 p-2 text-white/70 hover:text-white z-50"
            data-testid="lightbox-close"
          >
            <X size={32} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            className="absolute left-4 lg:left-8 p-3 bg-white/10 rounded-full text-white hover:bg-white/20"
            data-testid="lightbox-prev"
          >
            <ChevronLeft size={28} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            className="absolute right-4 lg:right-8 p-3 bg-white/10 rounded-full text-white hover:bg-white/20"
            data-testid="lightbox-next"
          >
            <ChevronRight size={28} />
          </button>
          <div className="max-w-5xl max-h-[85vh] mx-4 text-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={lightbox.items[lightbox.index].image_url}
              alt={lightbox.items[lightbox.index].title || ''}
              className="max-w-full max-h-[70vh] object-contain rounded-lg mx-auto"
            />
            <div className="mt-4">
              {lightbox.items[lightbox.index].title && (
                <h3 className="text-2xl font-bold text-white">{lightbox.items[lightbox.index].title}</h3>
              )}
              {lightbox.items[lightbox.index].location && (
                <p className="text-neutral-400 flex items-center justify-center gap-2 mt-2">
                  <MapPin size={16} />
                  {lightbox.items[lightbox.index].location}
                </p>
              )}
              <p className="text-neutral-300 mt-2 max-w-2xl mx-auto">{lightbox.items[lightbox.index].description}</p>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default TravelPage;
